/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {
  fetchChannels,
  fetchChannelDetails,
  fetchChannelStreams
} from 'services/channel.service';
import {IChannel, IMember} from 'interfaces/channelProps';
import {IAppStore} from 'store/reducers';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

export const CHANNELS_FETCHING = 'CHANNELS_FETCHING' as const;
export const CHANNELS_DONE_FETCHING = 'CHANNELS_DONE_FETCHING' as const;
export const CHANNELS_ERROR_FETCHING = 'CHANNELS_ERROR_FETCHING' as const;
export const CHANNEL_REMOVE = 'CHANNEL_REMOVE' as const;

export const KILL_CHANNEL_REQUEST = 'KILL_CHANNEL_REQUEST' as const;
export const KILL_CHANNEL_DONE = 'KILL_CHANNEL_DONE' as const;
export const KILL_CHANNEL_FAILED = 'KILL_CHANNEL_FAILED' as const;

export const SET_STREAMING_PROPERTY = 'SET_STREAMING_PROPERTY' as const;

export const CHANNEL_DETAILS_FETCHING = 'CHANNEL_DETAILS_FETCHING' as const;
export const CHANNEL_DETAILS_FETCHING_DONE = 'CHANNEL_DETAILS_FETCHING_DONE' as const;
export const CHANNEL_DETAILS_FETCHING_ERROR = 'CHANNEL_DETAILS_FETCHING_ERROR' as const;

export const CHANNEL_STREAMS_FETCHING = 'CHANNEL_STREAMS_FETCHING' as const;
export const CHANNEL_STREAMS_FETCHING_DONE = 'CHANNEL_STREAMS_FETCHING_DONE' as const;
export const CHANNEL_STREAMS_FETCHING_ERROR = 'CHANNEL_STREAMS_FETCHING_ERROR' as const;

export const channelsSelector = ({channels}: IAppStore): IAppStore['channels'] => channels;
export const channelDetailsSelector = ({channelDetails}: IAppStore): IAppStore['channelDetails'] => channelDetails;
export const channelStreamsSelector = ({channelStreams}: IAppStore): IAppStore['channelStreams'] => channelStreams;

interface IRequestChannelsAction {
  type: typeof CHANNELS_FETCHING;
}
interface IReceiveChannelsAction {
  type: typeof CHANNELS_DONE_FETCHING;
  channelList: IChannel[];
}
interface IFailedChannelsAction {
  type: typeof CHANNELS_ERROR_FETCHING;
  error: null | IPortalError;
}
export type FetchingChannelsActions =
  | IRequestChannelsAction
  | IReceiveChannelsAction
  | IFailedChannelsAction;
interface IGetChannelsActions {
  request: () => IRequestChannelsAction;
  receive: (channelList: IChannel[]) => IReceiveChannelsAction;
  failed: (error: null | IPortalError) => IFailedChannelsAction;
}
export const getChannelsActions: IGetChannelsActions = {
  request: () => ({type: CHANNELS_FETCHING}),
  receive: channelList => ({
    type: CHANNELS_DONE_FETCHING,
    channelList
  }),
  failed: error => ({
    type: CHANNELS_ERROR_FETCHING,
    error
  })
};

export const listChannels = () => async(
  dispatch: Dispatch<FetchingChannelsActions>
): Promise<IChannel[]> => {
  const {receive, request, failed} = getChannelsActions;

  dispatch(request());

  try {
    const channelList: IChannel[] = await fetchChannels();

    dispatch(receive(channelList));

    return channelList;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

interface IRemoveChannelAction {
  type: typeof CHANNEL_REMOVE;
  channelId: string;
}
export const removeChannel = (channelId: string): IRemoveChannelAction => ({
  type: CHANNEL_REMOVE,
  channelId
});

interface ISetStreamingPropertyAction {
  type: typeof SET_STREAMING_PROPERTY;
  value: boolean;
}

export const setStreamingProperty = (value: boolean): ISetStreamingPropertyAction => ({
  type: SET_STREAMING_PROPERTY,
  value
});

interface IFetchChannelDetailsRequest {
  type: typeof CHANNEL_DETAILS_FETCHING;
}

interface IFetchChannelDetailsSuccess {
  type: typeof CHANNEL_DETAILS_FETCHING_DONE;
  payload: IChannel;
}

interface IFetchChannelDetailsFailed {
  type: typeof CHANNEL_DETAILS_FETCHING_ERROR;
  error: null | IPortalError;
}

export type FetchChannelDetailsActions =
  | IFetchChannelDetailsRequest
  | IFetchChannelDetailsSuccess
  | IFetchChannelDetailsFailed;

interface IFetchChannelDetailsActions {
  request: () => IFetchChannelDetailsRequest;
  receive: (payload: IChannel) => IFetchChannelDetailsSuccess;
  failed: (error: null | IPortalError) => IFetchChannelDetailsFailed;
}

const fetchChannelDetailsActions: IFetchChannelDetailsActions = {
  request: () => ({type: CHANNEL_DETAILS_FETCHING}),
  receive: payload => ({
    type: CHANNEL_DETAILS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: CHANNEL_DETAILS_FETCHING_ERROR,
    error
  })
};

export const getChannelDetails = (channelId: string) => async(dispatch: Dispatch): Promise<IChannel> => {
  const {receive, request, failed} = fetchChannelDetailsActions;

  dispatch(request());

  try {
    const channel: IChannel = await fetchChannelDetails(channelId);

    dispatch(receive(channel));

    return channel;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

interface IFetchChannelStreamsRequest {
  type: typeof CHANNEL_STREAMS_FETCHING;
}

interface IFetchChannelStreamsSuccess {
  type: typeof CHANNEL_STREAMS_FETCHING_DONE;
  payload: IMember[];
}

interface IFetchChannelStreamsFailed {
  type: typeof CHANNEL_STREAMS_FETCHING_ERROR;
  error: null | IPortalError;
}

export type FetchChannelStreamsActions =
  | IFetchChannelStreamsRequest
  | IFetchChannelStreamsSuccess
  | IFetchChannelStreamsFailed;

interface IFetchChannelStreamsActions {
  request: () => IFetchChannelStreamsRequest;
  receive: (payload: IMember[]) => IFetchChannelStreamsSuccess;
  failed: (error: null | IPortalError) => IFetchChannelStreamsFailed;
}

const fetchChannelStreamsActions: IFetchChannelStreamsActions = {
  request: () => ({type: CHANNEL_STREAMS_FETCHING}),
  receive: payload => ({
    type: CHANNEL_STREAMS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: CHANNEL_STREAMS_FETCHING_ERROR,
    error
  })
};

export const getChannelStreams = (channelId: string) => async(dispatch: Dispatch): Promise<IMember[]> => {
  const {receive, request, failed} = fetchChannelStreamsActions;

  dispatch(request());

  try {
    const channelStreams: IMember[] = await fetchChannelStreams(channelId);

    dispatch(receive(channelStreams));

    return channelStreams;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export type ChannelsActions =
  | FetchingChannelsActions
  | FetchChannelDetailsActions
  | IRemoveChannelAction
  | ISetStreamingPropertyAction
  | FetchChannelStreamsActions;